/**
 * Additional Trades Select Component
 *
 * A searchable multi-select combobox for selecting additional trades.
 * Uses Popover + Command components for efficient search through 50+ trades.
 * Trades are grouped by section for easier navigation.
 * Supports subscription tier limits (maxTrades).
 */

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown, Briefcase, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTrades, Trade, groupTradesBySection } from '@/hooks/useTrades';

interface AdditionalTradesSelectProps {
  value: Trade[];
  onChange: (trades: Trade[]) => void;
  disabled?: boolean;
  maxTrades?: number;
  showLabel?: boolean;
  onLimitReached?: () => void;
}

export function AdditionalTradesSelect({
  value,
  onChange,
  disabled = false,
  maxTrades = Infinity,
  showLabel = true,
  onLimitReached,
}: AdditionalTradesSelectProps) {
  const [open, setOpen] = React.useState(false);
  const { data: allTrades, isLoading, error: queryError, refetch } = useTrades();

  // Group trades by section for the dropdown display
  const groupedTrades = React.useMemo(() => groupTradesBySection(allTrades || []), [allTrades]);

  const handleSelect = (trade: Trade) => {
    if (disabled) return;

    const isSelected = value.some(t => t.id === trade.id);

    if (isSelected) {
      // Remove the trade
      onChange(value.filter(t => t.id !== trade.id));
    } else {
      // Check limit before adding
      if (value.length >= maxTrades) {
        onLimitReached?.();
        return;
      }
      // Add the trade
      onChange([...value, trade]);
    }
  };

  const removeTrade = (tradeId: string) => {
    if (disabled) return;
    onChange(value.filter(t => t.id !== tradeId));
  };

  const error = queryError ? 'Unable to load trades. Please try again.' : null;

  if (error) {
    return (
      <div className="space-y-3">
        {showLabel && (
          <label className="text-sm font-medium text-gray-700">
            Select Additional Trades
          </label>
        )}
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="sm"
            className="mt-2"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {showLabel && <Skeleton className="h-5 w-40" />}
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showLabel && (
        <label className="text-sm font-medium text-gray-700">
          Select Additional Trades
        </label>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between h-auto min-h-[44px] py-2 px-3",
              value.length === 0 && "text-muted-foreground"
            )}
          >
            {value.length > 0 ? (
              <span className="text-left truncate">
                {value.length} trade{value.length !== 1 ? 's' : ''} selected
              </span>
            ) : (
              <span>Search and select trades...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command
            filter={(value, search) => {
              if (!search) return 1;
              return value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0;
            }}
          >
            <CommandInput placeholder="Search trades..." className="h-10" />
            <CommandList className="max-h-[300px]">
              <CommandEmpty>No trade found.</CommandEmpty>
              {groupedTrades.map((section) => (
                <CommandGroup key={section.section || 'Other'} heading={section.section || 'Other'}>
                  {section.trades.map((trade) => {
                    const isSelected = value.some(t => t.id === trade.id);
                    const isDisabled = !isSelected && value.length >= maxTrades;

                    return (
                      <CommandItem
                        key={trade.id}
                        value={`${trade.name} ${trade.section || ''}`}
                        onSelect={() => handleSelect(trade)}
                        disabled={isDisabled}
                        className={cn(
                          "flex items-center gap-2 py-2",
                          isDisabled && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className={cn(
                          "w-6 h-6 rounded flex items-center justify-center flex-shrink-0",
                          isSelected ? "bg-blue-500" : "bg-gray-100"
                        )}>
                          {isSelected ? (
                            <Check className="w-3.5 h-3.5 text-white" />
                          ) : (
                            <Briefcase className="w-3.5 h-3.5 text-gray-600" />
                          )}
                        </div>
                        <span className="flex-1 truncate">{trade.name}</span>
                        {isSelected && (
                          <Check className="h-4 w-4 flex-shrink-0 text-blue-600" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected trades as removable tags */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((trade) => (
            <span
              key={trade.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
            >
              {trade.name}
              <button
                type="button"
                onClick={() => removeTrade(trade.id)}
                disabled={disabled}
                className="hover:bg-blue-200 rounded-full p-0.5 disabled:opacity-50"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
